import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Grid, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import { validateBulkUploadCsv, submitBulkUploadCsv } from '../../../admin/Services/BulkUploadService';
import { SHOW_NOTIFICATION } from '../../../constants';
import './BulkUpload.css';

export default function BulkUploadComponent() {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [submissionResult, setSubmissionResult] = useState(null);

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setValidationResult(null);
        setSubmissionResult(null);
      } else {
        showNotification('error', 'Only CSV files are supported.');
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      const file = event.dataTransfer.files[0];
      if (file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setValidationResult(null);
        setSubmissionResult(null);
      } else {
        showNotification('error', 'Only CSV files are supported.');
      }
    }
  };

  const showNotification = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: type,
        message: message,
      },
    });
  };

  const handleValidate = async () => {
    if (!selectedFile) return;
    setIsValidating(true);
    setValidationResult(null);
    setSubmissionResult(null);

    const response = await validateBulkUploadCsv(selectedFile);
    setIsValidating(false);

    // ErrorSwitchCase returns { status: 200 (number), isError: false, data: { data: DTO, ... } }
    if (response && response.isError === false) {
      // Admin controller wraps DTO in ResponseObject.setData(),
      // so actual validation DTO is at response.data.data
      const validationData = response.data?.data;
      if (validationData) {
        setValidationResult(validationData);
        if (validationData.allPassed) {
          showNotification('success', 'CSV validation successful! All records are valid.');
        } else {
          showNotification('warning', 'CSV validation found errors in some records.');
        }
      } else {
        showNotification('error', 'Unexpected response format from server.');
      }
    } else {
      showNotification('error', response?.message || 'Failed to validate CSV file.');
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    setIsSubmitting(true);

    const response = await submitBulkUploadCsv(selectedFile);
    setIsSubmitting(false);

    if (response && response.isError === false) {
      const submitData = response.data?.data;
      if (submitData) {
        setSubmissionResult(submitData);
        showNotification('success', submitData.message || 'Bulk upload processing complete.');
        setSelectedFile(null);
        setValidationResult(null);
      } else {
        showNotification('error', 'Unexpected response format from server.');
      }
    } else {
      showNotification('error', response?.message || 'Failed to submit bulk upload.');
    }
  };

  const downloadReport = () => {
    if (!validationResult || !validationResult.validationReportCsv) return;
    try {
      const byteCharacters = atob(validationResult.validationReportCsv);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'text/csv;charset=utf-8;' });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `validation_report_${selectedFile?.name || 'report'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showNotification('error', 'Error generating report download: ' + error.message);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setSubmissionResult(null);
  };

  return (
    <Box className="bulk-upload-container">
      <Paper className="bulk-upload-header-card" elevation={3}>
        <Typography variant="h4" className="bulk-upload-title">
          CSV Bulk Record Ingestion
        </Typography>
        <Typography variant="body1" className="bulk-upload-subtitle">
          Upload a CSV file containing employee workday records (Leaves, WFH, External Hours, Client Office Hours, and Remote Office Hours) to import them in bulk.
          For Remote Office use type REMOTE_OFFICE (aliases: REMOTE OFFICE, REMOTE OFFICE HOURS). Working Hours is the total for the From–To date range and is distributed evenly across weekdays (weekends skipped).
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Upload Card */}
        <Grid item xs={12} md={validationResult ? 6 : 12}>
          <Paper className="upload-section-card" elevation={2}>
            <Typography variant="h6" className="card-section-title">
              1. Choose CSV File
            </Typography>
            <div 
              className={`dropzone-area ${selectedFile ? 'has-file' : ''}`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="csv-file-input"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="csv-file-input" className="dropzone-label">
                <CloudUploadIcon className="upload-icon" />
                {selectedFile ? (
                  <Box className="file-info-box">
                    <Typography className="selected-filename">{selectedFile.name}</Typography>
                    <Typography className="selected-filesize">
                      ({(selectedFile.size / 1024).toFixed(2)} KB)
                    </Typography>
                  </Box>
                ) : (
                  <Typography className="dropzone-text">
                    Drag and drop your CSV file here, or <span>Browse</span>
                  </Typography>
                )}
              </label>
            </div>

            {selectedFile && (
              <Box className="action-buttons-box">
                <Button
                  variant="contained"
                  onClick={handleValidate}
                  disabled={isValidating || isSubmitting}
                  className="validate-btn"
                  startIcon={isValidating ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {isValidating ? 'Validating...' : 'Validate CSV'}
                </Button>
                
                {validationResult && validationResult.allPassed && (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="submit-btn"
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutlineIcon />}
                  >
                    {isSubmitting ? 'Ingesting...' : 'Ingest Records'}
                  </Button>
                )}
              </Box>
            )}
          </Paper>

          {submissionResult && (
            <Paper className="results-alert-card" elevation={2}>
              <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
                <Typography variant="h6">Submission Successful</Typography>
                <Typography variant="body2">
                  Successfully ingested <strong>{submissionResult.successfulRecords}</strong> records.
                  {submissionResult.failedRecords > 0 && ` Failed: ${submissionResult.failedRecords}.`}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Leave: <strong>{submissionResult.leaveRecords || 0}</strong> | 
                  WFH: <strong>{submissionResult.wfhRecords || 0}</strong> | 
                  External / Client Office Hours: <strong>{submissionResult.externalHoursRecords || 0}</strong>
                </Typography>
              </Alert>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button variant="outlined" onClick={handleReset}>
                  Upload Another File
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Validation Report Card */}
        {validationResult && (
          <Grid item xs={12} md={6}>
            <Paper className="validation-report-card" elevation={2}>
              <Typography variant="h6" className="card-section-title">
                2. Validation Report
              </Typography>
              
              <Box className="metrics-box">
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box className="metric-card total">
                      <Typography className="metric-value">{validationResult.totalRows}</Typography>
                      <Typography className="metric-label">Total Rows</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box className="metric-card passed">
                      <Typography className="metric-value">{validationResult.passCount}</Typography>
                      <Typography className="metric-label">Passed</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box className={`metric-card ${validationResult.failCount > 0 ? 'failed' : 'no-failed'}`}>
                      <Typography className="metric-value">{validationResult.failCount}</Typography>
                      <Typography className="metric-label">Failed</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {validationResult.allPassed ? (
                <Box className="status-banner success">
                  <CheckCircleOutlineIcon className="status-icon" />
                  <Typography>CSV is fully valid. Click "Ingest Records" to save in Database.</Typography>
                </Box>
              ) : (
                <Box className="status-banner error">
                  <ErrorOutlineIcon className="status-icon" />
                  <Typography>Validation failed for {validationResult.failCount} rows. Please correct and retry.</Typography>
                </Box>
              )}

              {validationResult.failCount > 0 && (
                <Box className="error-actions-box">
                  <Button 
                    variant="outlined" 
                    onClick={downloadReport} 
                    className="download-report-btn"
                  >
                    Download Error Report CSV
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>
        )}

        {/* Sample Failures Table */}
        {validationResult && validationResult.sampleFailures && validationResult.sampleFailures.length > 0 && (
          <Grid item xs={12}>
            <Paper className="failures-table-card" elevation={2}>
              <Typography variant="h6" className="card-section-title">
                Sample Validation Failures (Top {validationResult.sampleFailures.length}
                {validationResult.failCount > 10 ? ` of ${validationResult.failCount} — download full report for all details` : ''})
              </Typography>
              <TableContainer className="failures-table-container">
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="table-header-cell">Row</TableCell>
                      <TableCell className="table-header-cell">Email</TableCell>
                      <TableCell className="table-header-cell">Name</TableCell>
                      <TableCell className="table-header-cell">Type</TableCell>
                      <TableCell className="table-header-cell">Errors / Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResult.sampleFailures.map((row) => (
                      <TableRow key={row.rowNumber} hover className="table-body-row">
                        <TableCell className="table-body-cell bold">{row.rowNumber}</TableCell>
                        <TableCell className="table-body-cell">{row.email || '-'}</TableCell>
                        <TableCell className="table-body-cell">{row.name || '-'}</TableCell>
                        <TableCell className="table-body-cell">{row.workdayType || '-'}</TableCell>
                        <TableCell className="table-body-cell error-text">{row.comments}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
